import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { isAdminOrSuperAdmin } from '../utils/auth';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

const EditClub: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    state: '',
    city: '',
    address: '',
    phoneNumber: '',
    email: '',
    website: '',
    membershipFee: '',
    teamFee: '',
    primaryColor: '#000000',
    secondaryColor: '#FFFFFF',
    logo: '',
    multiTeamDiscountType: '',
    multiTeamDiscountValue: ''
  });

  useEffect(() => {
    const fetchClub = async () => {
      try {
        const response = await api.get(`/clubs/${id}`);
        const club = response.data;

        // Check authorization
        if (user && club.administratorId !== user.id && !isAdminOrSuperAdmin(user.profileType)) {
          navigate('/clubs');
          return;
        }

        setFormData({
          name: club.name || '',
          description: club.description || '',
          state: club.state || '',
          city: club.city || '',
          address: club.address || '',
          phoneNumber: club.phoneNumber || '',
          email: club.email || '',
          website: club.website || '',
          membershipFee: club.membershipFee != null ? String(club.membershipFee) : '',
          teamFee: club.teamFee != null ? String(club.teamFee) : '',
          primaryColor: club.colors?.primary || '#000000',
          secondaryColor: club.colors?.secondary || '#FFFFFF',
          logo: club.logo || '',
          multiTeamDiscountType: club.multiTeamDiscountType || '',
          multiTeamDiscountValue: club.multiTeamDiscountValue != null ? String(club.multiTeamDiscountValue) : ''
        });
      } catch (err) {
        console.error('Error fetching club:', err);
        setError('Failed to load club data');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchClub();
    }
  }, [id, user, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const submitData: Record<string, any> = {
        name: formData.name,
        description: formData.description || null,
        state: formData.state || null,
        city: formData.city || null,
        address: formData.address || null,
        phoneNumber: formData.phoneNumber || null,
        email: formData.email || null,
        website: formData.website || null,
        logo: formData.logo || null,
        colors: {
          primary: formData.primaryColor,
          secondary: formData.secondaryColor
        },
        membershipFee: formData.membershipFee ? parseFloat(formData.membershipFee) : null,
        teamFee: formData.teamFee ? parseFloat(formData.teamFee) : null,
        multiTeamDiscountType: formData.multiTeamDiscountType || null,
        multiTeamDiscountValue: formData.multiTeamDiscountValue ? parseFloat(formData.multiTeamDiscountValue) : null
      };

      await api.put(`/clubs/${id}`, submitData);
      navigate(`/clubs/${id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update club');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container">
      <h1>Edit Club</h1>
      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label>Club Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="e.g., Thunder FC Club"
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            placeholder="Brief description of the club"
          />
        </div>

        <div className="form-group">
          <label>Logo URL</label>
          <input
            type="url"
            name="logo"
            value={formData.logo}
            onChange={handleChange}
            placeholder="https://example.com/logo.png"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>Primary Color</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="color"
                name="primaryColor"
                value={formData.primaryColor}
                onChange={handleChange}
                style={{ width: '60px', height: '40px', border: 'none', cursor: 'pointer' }}
              />
              <input
                type="text"
                value={formData.primaryColor}
                onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                style={{ flex: 1 }}
                placeholder="#000000"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Secondary Color</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="color"
                name="secondaryColor"
                value={formData.secondaryColor}
                onChange={handleChange}
                style={{ width: '60px', height: '40px', border: 'none', cursor: 'pointer' }}
              />
              <input
                type="text"
                value={formData.secondaryColor}
                onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                style={{ flex: 1 }}
                placeholder="#FFFFFF"
              />
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>State</label>
            <select
              name="state"
              value={formData.state}
              onChange={handleChange}
            >
              <option value="">Select State</option>
              {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>City</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="e.g., Dallas"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Address</label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Street address"
          />
        </div>

        <div className="form-group">
          <label>Phone Number</label>
          <input
            type="tel"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder="(555) 123-4567"
          />
        </div>

        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="club@example.com"
          />
        </div>

        <div className="form-group">
          <label>Website</label>
          <input
            type="url"
            name="website"
            value={formData.website}
            onChange={handleChange}
            placeholder="https://www.example.com"
          />
        </div>

        <h3 style={{ marginTop: '20px' }}>Fees & Discounts</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>Membership Fee ($)</label>
            <input
              type="number"
              name="membershipFee"
              value={formData.membershipFee}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>

          <div className="form-group">
            <label>Default Team Fee ($)</label>
            <input
              type="number"
              name="teamFee"
              value={formData.teamFee}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>Multi-Team Discount Type</label>
            <select
              name="multiTeamDiscountType"
              value={formData.multiTeamDiscountType}
              onChange={handleChange}
            >
              <option value="">No discount</option>
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed Amount</option>
            </select>
          </div>

          {formData.multiTeamDiscountType && (
            <div className="form-group">
              <label>
                Discount Value {formData.multiTeamDiscountType === 'percentage' ? '(%)' : '($)'}
              </label>
              <input
                type="number"
                name="multiTeamDiscountValue"
                value={formData.multiTeamDiscountValue}
                onChange={handleChange}
                placeholder="0"
                step="0.01"
                min="0"
              />
            </div>
          )}
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(`/clubs/${id}`)}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditClub;
